import { alpha, createTheme } from "@mui/material/styles";

/* -------------------------------------------------------------------------- */
/*  Raw palette — the only place hex values live                               */
/* -------------------------------------------------------------------------- */

/**
 * Phosphor cyan — the "live signal" accent. Used for primary CTAs, active
 * nav, focus rings, signal-trace highlights, and every place the UI shows
 * "this is currently happening / connected / live". One accent at a time;
 * never runs simultaneously with the editorial amber.
 */
const trace = {
  /** Default phosphor cyan. */
  500: "#5be0c8",
  /** Brighter for hover. */
  400: "#7defd4",
  /** Soft text-on-surface tint. */
  300: "#a9efde",
  /** Deep accent for borders / active surfaces. */
  800: "#1d4d44",
} as const;

/**
 * Trace amber — the "editorial" accent. Used for AI provenance, EDA
 * imports, suggested renames, lossy round-trips, and any moment that says
 * "a human or the AI made a judgement call here". Visually distinct from
 * the live-signal trace so the eye learns to read them apart.
 */
const editorial = {
  500: "#d4a05c",
  400: "#e8b574",
  300: "#f0c794",
  800: "#5a4324",
} as const;

/**
 * Blueprint indigo surface ramp — deeper and slightly bluer than the
 * previous slate ramp. Reads as drafting paper under a desk lamp once
 * the crosshatch grid texture is layered on top via `blueprintBg()`.
 */
const ink = {
  /** Page background — deepest. */
  bg: "#0a121f",
  /** Sidebar / persistent chrome. */
  surface: "#0f1828",
  /** Cards, inputs, raised pills. */
  raised: "#172033",
  /** Hover-raised — one notch above raised. */
  raisedHover: "#1d2a44",
  /** Tooltips / menus / overlays. */
  overlay: "#1e2c4a",
  /** Sunken — schematic frame, code blocks. */
  sunken: "#070d18",
  /** Hairline border — for 0.5–1px outlines on every panel. */
  hairline: "#1c2740",
  /** Stronger border — only for hovered or focused outlines. */
  hairlineStrong: "#2a3a5e",
} as const;

const content = {
  primary: "#e7edf6",
  secondary: "#8aa0bd",
  disabled: "#5a6f90",
} as const;

/**
 * Severity — kept warm enough not to scream on dense surfaces. The error
 * coral is intentionally softer than #ef4444 so a sea of warning chips
 * doesn't fatigue the eye.
 */
const status = {
  ok: "#7bd690",
  warn: "#e6b34c",
  err: "#f06464",
  info: "#6cc6ec",
} as const;

/* -------------------------------------------------------------------------- */
/*  Module augmentation — lets `sx={{ bgcolor: "surface.raised" }}` type-check */
/* -------------------------------------------------------------------------- */

declare module "@mui/material/styles" {
  interface Palette {
    surface: {
      default: string;
      raised: string;
      raisedHover: string;
      sunken: string;
      overlay: string;
    };
    border: {
      default: string;
      strong: string;
      accent: string;
    };
    /** "Live signal" accent — phosphor cyan. Same as `primary.main`; kept
     *  as a separate name so feature code can reach for `accent.soft`
     *  without thinking about whether to use primary or secondary. */
    accent: {
      main: string;
      soft: string;
      softer: string;
      glow: string;
      contrastText: string;
    };
    /** "Editorial" accent — trace amber. AI provenance, imports, suggested
     *  renames. Use sparingly so it stays meaningful when it appears. */
    editorial: {
      main: string;
      soft: string;
      softer: string;
      contrastText: string;
    };
  }
  interface PaletteOptions {
    surface?: Palette["surface"];
    border?: Palette["border"];
    accent?: Palette["accent"];
    editorial?: Palette["editorial"];
  }

  /**
   * Motion + font tokens so feature code doesn't hard-code timing strings
   * or font stacks at every call site.
   */
  interface Theme {
    motion: {
      fast: string;
      base: string;
      slow: string;
      /** 220ms — the snapshot-pill emergence stroke. */
      pill: string;
      /** 360ms — the refdes annotation reveal cross-fade. */
      reveal: string;
      /** 600ms — the DRC pulse ring. */
      pulse: string;
      /** 220ms with a gentle overshoot — sidebar/copilot reactions. */
      spring: string;
    };
    fonts: { sans: string; mono: string };
  }
  interface ThemeOptions {
    motion?: Theme["motion"];
    fonts?: Theme["fonts"];
  }
}

const STANDARD_EASE = "cubic-bezier(0.4, 0, 0.2, 1)";
// Gentle overshoot — used for chrome reactions (rail open/close, copilot
// slide) so the layout feels alive without jarring the eye.
const SPRING_EASE = "cubic-bezier(0.34, 1.36, 0.64, 1)";
const motion = {
  fast: `120ms ${STANDARD_EASE}`,
  base: `180ms ${STANDARD_EASE}`,
  slow: `260ms ${STANDARD_EASE}`,
  pill: `220ms ${STANDARD_EASE}`,
  reveal: `360ms ${STANDARD_EASE}`,
  pulse: `600ms ${STANDARD_EASE}`,
  spring: `220ms ${SPRING_EASE}`,
} as const;

/**
 * Type pairing: Inter Tight (display + body) for chrome, JetBrains Mono
 * for refdes / nets / IDs / counts. Inter Tight is tighter than Inter and
 * pairs better with engineering surfaces. Both are loaded as variable
 * fonts at the entry point — see `main.tsx`.
 */
const fonts = {
  sans: '"Inter Tight Variable", "Inter Tight", "Inter", "system-ui", -apple-system, sans-serif',
  mono: '"JetBrains Mono", "Geist Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

/* -------------------------------------------------------------------------- */
/*  Theme                                                                      */
/* -------------------------------------------------------------------------- */

export const darkTheme = createTheme({
  motion,
  fonts,
  palette: {
    mode: "dark",
    primary: {
      main: trace[500],
      light: trace[400],
      dark: trace[800],
      contrastText: "#0a121f",
    },
    secondary: {
      main: editorial[500],
      light: editorial[400],
      dark: editorial[800],
      contrastText: "#0a121f",
    },
    background: {
      default: ink.bg,
      paper: ink.raised,
    },
    text: {
      primary: content.primary,
      secondary: content.secondary,
      disabled: content.disabled,
    },
    divider: ink.hairline,
    success: { main: status.ok },
    warning: { main: status.warn },
    error: { main: status.err },
    info: { main: status.info },
    surface: {
      default: ink.surface,
      raised: ink.raised,
      raisedHover: ink.raisedHover,
      sunken: ink.sunken,
      overlay: ink.overlay,
    },
    border: {
      default: ink.hairline,
      strong: ink.hairlineStrong,
      accent: trace[800],
    },
    accent: {
      main: trace[500],
      soft: alpha(trace[500], 0.16),
      softer: alpha(trace[500], 0.08),
      glow: `0 0 12px ${alpha(trace[500], 0.45)}`,
      contrastText: trace[300],
    },
    editorial: {
      main: editorial[500],
      soft: alpha(editorial[500], 0.16),
      softer: alpha(editorial[500], 0.08),
      contrastText: editorial[300],
    },
  },

  typography: {
    fontFamily: fonts.sans,
    // Tabular numerals on every data-dense variant so columns of refdes,
    // version chips, counts, and prices don't drift between rows.
    h1: {
      fontSize: "1.875rem",
      fontWeight: 700,
      letterSpacing: "-0.022em",
      lineHeight: 1.15,
      "@media (min-width:900px)": { fontSize: "2.25rem" },
    },
    h2: {
      fontSize: "1.5rem",
      fontWeight: 700,
      letterSpacing: "-0.018em",
      lineHeight: 1.2,
      "@media (min-width:900px)": { fontSize: "1.75rem" },
    },
    h3: {
      fontSize: "1.25rem",
      fontWeight: 600,
      letterSpacing: "-0.012em",
      lineHeight: 1.3,
      "@media (min-width:900px)": { fontSize: "1.375rem" },
    },
    h4: {
      fontSize: "1.0625rem",
      fontWeight: 600,
      letterSpacing: "-0.008em",
      lineHeight: 1.35,
      "@media (min-width:900px)": { fontSize: "1.125rem" },
    },
    h5: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.4 },
    subtitle1: { fontSize: "0.9375rem", fontWeight: 500, lineHeight: 1.45 },
    subtitle2: {
      fontSize: "0.8125rem",
      fontWeight: 500,
      lineHeight: 1.4,
      color: content.secondary,
    },
    body1: { fontSize: "0.9375rem", lineHeight: 1.6 },
    body2: {
      fontSize: "0.8125rem",
      lineHeight: 1.55,
      fontVariantNumeric: "tabular-nums",
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.4,
      letterSpacing: "0.01em",
      fontVariantNumeric: "tabular-nums",
    },
    overline: {
      fontSize: "0.6875rem",
      fontWeight: 600,
      letterSpacing: "0.085em",
      textTransform: "uppercase",
      lineHeight: 1.4,
    },
    button: {
      fontSize: "0.8125rem",
      fontWeight: 500,
      letterSpacing: "0.005em",
    },
  },

  shape: { borderRadius: 8 },

  components: {
    /* -------------------------------------------------- global resets ---- */
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `${ink.hairlineStrong} ${ink.bg}`,
          "&::-webkit-scrollbar": { width: "8px", height: "8px" },
          "&::-webkit-scrollbar-track": { background: ink.bg },
          "&::-webkit-scrollbar-thumb": {
            background: ink.hairlineStrong,
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": { background: trace[500] },
        },
        // Universal focus ring — phosphor glow.
        "*:focus-visible": {
          outline: `2px solid ${trace[400]}`,
          outlineOffset: "2px",
          borderRadius: "4px",
        },
        "::selection": {
          background: alpha(trace[500], 0.35),
          color: content.primary,
        },
        // Keyframes for the three signature motion moments.
        "@keyframes traceStrokeIn": {
          from: { strokeDashoffset: "var(--len, 100)", opacity: 0.4 },
          to: { strokeDashoffset: "0", opacity: 1 },
        },
        "@keyframes drcPulseRing": {
          "0%": {
            transform: "scale(0.6)",
            opacity: 0.55,
            boxShadow: `0 0 0 0 ${alpha(status.err, 0.6)}`,
          },
          "100%": {
            transform: "scale(1.6)",
            opacity: 0,
            boxShadow: `0 0 0 14px ${alpha(status.err, 0)}`,
          },
        },
        "@keyframes refdesReveal": {
          "0%": {
            opacity: 0,
            transform: "translateY(2px)",
            filter: "blur(4px)",
          },
          "100%": {
            opacity: 1,
            transform: "translateY(0)",
            filter: "blur(0)",
          },
        },
      },
    },

    /* ------------------------------------------------------ surfaces ----- */
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: "none" },
        outlined: { borderColor: ink.hairline },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: "transparent" },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderBottom: `1px solid ${ink.hairline}`,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundImage: "none", borderColor: ink.hairline },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: "none",
          background: ink.raised,
          border: `1px solid ${ink.hairline}`,
          borderRadius: 12,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: { root: { paddingTop: 16 } },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundImage: "none",
          background: ink.overlay,
          border: `1px solid ${ink.hairline}`,
          borderRadius: 8,
          marginTop: 4,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          borderRadius: 6,
          margin: "2px 6px",
          minHeight: 34,
          "&:hover": { backgroundColor: alpha(trace[500], 0.1) },
          "&.Mui-selected": {
            backgroundColor: alpha(trace[500], 0.16),
            "&:hover": { backgroundColor: alpha(trace[500], 0.22) },
          },
        },
      },
    },

    /* ------------------------------------------------------ buttons ------ */
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          borderRadius: 8,
          paddingInline: 14,
        },
        sizeSmall: {
          fontSize: "0.78rem",
          paddingInline: 10,
          minHeight: 30,
        },
        containedPrimary: {
          background: trace[500],
          color: ink.bg,
          boxShadow: `0 0 0 1px ${alpha(trace[500], 0.4)}, ${alpha(trace[500], 0.2)} 0 4px 16px`,
          "&:hover": {
            background: trace[400],
            boxShadow: `0 0 0 1px ${alpha(trace[400], 0.5)}, ${alpha(trace[400], 0.32)} 0 6px 18px`,
          },
        },
        outlined: {
          borderColor: ink.hairline,
          color: content.secondary,
          "&:hover": {
            borderColor: trace[500],
            color: content.primary,
            backgroundColor: alpha(trace[500], 0.08),
          },
        },
        text: {
          color: content.secondary,
          "&:hover": {
            color: content.primary,
            backgroundColor: alpha(trace[500], 0.08),
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          color: content.secondary,
          borderRadius: 8,
          "&:hover": {
            color: content.primary,
            backgroundColor: alpha(trace[500], 0.1),
          },
        },
      },
    },

    /* ------------------------------------------------------ inputs ------- */
    MuiTextField: {
      defaultProps: { size: "small" },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: ink.raised,
            borderRadius: 8,
            "& fieldset": { borderColor: ink.hairline },
            "&:hover fieldset": { borderColor: ink.hairlineStrong },
            "&.Mui-focused fieldset": {
              borderColor: trace[500],
              borderWidth: 1,
              boxShadow: `0 0 0 3px ${alpha(trace[500], 0.18)}`,
            },
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: { outlined: { backgroundColor: ink.raised } },
    },

    MuiInputLabel: {
      styleOverrides: { root: { fontSize: "0.8125rem" } },
    },

    /* ------------------------------------------------------ tabs --------- */
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderBottom: `1px solid ${ink.hairline}`,
        },
        indicator: { backgroundColor: trace[500], height: 2 },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.8125rem",
          minHeight: 40,
          color: content.secondary,
          "&.Mui-selected": { color: trace[300] },
        },
      },
    },

    /* ------------------------------------------------------ chips -------- */
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: "0.72rem",
          height: 22,
          fontWeight: 500,
          fontVariantNumeric: "tabular-nums",
        },
        outlined: { borderColor: ink.hairline },
      },
    },

    /* ------------------------------------------------------ lists -------- */
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingInline: 10,
          "&.Mui-selected": {
            backgroundColor: alpha(trace[500], 0.14),
            color: trace[300],
            "&:hover": { backgroundColor: alpha(trace[500], 0.2) },
          },
          "&:hover": { backgroundColor: alpha(trace[500], 0.08) },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 32, color: "inherit" } },
    },

    /* ------------------------------------------------------ misc --------- */
    MuiDivider: {
      styleOverrides: { root: { borderColor: ink.hairline } },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(trace[500], 0.12),
          borderRadius: 3,
          height: 4,
        },
        bar: {
          borderRadius: 3,
          background: `linear-gradient(90deg, ${trace[500]} 0%, ${trace[400]} 100%)`,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: "0.8125rem",
          border: `1px solid ${ink.hairline}`,
        },
        standardError: {
          backgroundColor: alpha(status.err, 0.1),
          color: "#fbcaca",
        },
        standardWarning: {
          backgroundColor: alpha(status.warn, 0.1),
          color: "#fadeb0",
        },
        standardSuccess: {
          backgroundColor: alpha(status.ok, 0.1),
          color: "#bcebc8",
        },
        standardInfo: {
          backgroundColor: alpha(status.info, 0.1),
          color: "#b8e0f3",
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: ink.overlay,
          border: `1px solid ${ink.hairline}`,
          fontSize: "0.75rem",
          fontWeight: 500,
          padding: "6px 10px",
          borderRadius: 6,
        },
        arrow: { color: ink.overlay },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: ink.raisedHover,
          color: content.primary,
          fontSize: "0.8125rem",
          fontWeight: 600,
        },
      },
    },

    /* ------------------------------------------------------ skeletons --- */
    MuiSkeleton: {
      defaultProps: { animation: "wave" },
      styleOverrides: {
        root: {
          backgroundColor: alpha(trace[500], 0.06),
          "&::after": {
            background: `linear-gradient(90deg, transparent, ${alpha(trace[500], 0.18)}, transparent)`,
          },
        },
      },
    },
  },
});
